import type { CoinMap, CoinType } from '../types'

const MAX_AMOUNT = 1_000_000_000

export interface CoinQuote {
  spend: number
  receive: number
  leftover: number
  minAmount: number
  ok: boolean
  valueCents: bigint
  receiveCents: bigint
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a
  let y = b < 0n ? -b : b
  while (y !== 0n) {
    const t = x % y
    x = y
    y = t
  }
  return x === 0n ? 1n : x
}

/** Same whole-coin rule as backend domain.QuoteCoinExchange. */
export function quoteCoinExchange(amount: number, fromCents: number, toCents: number): CoinQuote | null {
  if (!Number.isInteger(amount) || amount <= 0 || amount > MAX_AMOUNT) return null
  if (!Number.isInteger(fromCents) || !Number.isInteger(toCents) || fromCents < 1 || toCents < 1) return null

  const from = BigInt(fromCents)
  const to = BigInt(toCents)
  const g = gcd(from, to)
  const minReceive = from / g
  const minSpend = to / g
  const valueCents = BigInt(amount) * from
  const minAmount = Number(minSpend)

  if (BigInt(amount) < minSpend) {
    return {
      spend: 0,
      receive: 0,
      leftover: amount,
      minAmount,
      ok: false,
      valueCents,
      receiveCents: 0n,
    }
  }

  const chunks = BigInt(amount) / minSpend
  const spend = chunks * minSpend
  const receive = chunks * minReceive
  if (spend > BigInt(Number.MAX_SAFE_INTEGER) || receive > BigInt(Number.MAX_SAFE_INTEGER)) return null

  return {
    spend: Number(spend),
    receive: Number(receive),
    leftover: amount - Number(spend),
    minAmount,
    ok: true,
    valueCents,
    receiveCents: receive * to,
  }
}

export function walletValueCents(coins: CoinMap, rates: Partial<Record<CoinType, number>>): bigint {
  let sum = 0n
  for (const coin of ['bronze', 'silver', 'gold', 'diamond'] as const) {
    const count = coins[coin] || 0
    const cents = rates[coin] || 0
    if (count > 0 && cents > 0) sum += BigInt(count) * BigInt(cents)
  }
  return sum
}

export function formatEurCents(cents: bigint): string {
  const neg = cents < 0n
  const abs = neg ? -cents : cents
  const whole = (abs / 100n).toString()
  const frac = (abs % 100n).toString().padStart(2, '0')
  return `${neg ? '-' : ''}${whole}.${frac} €`
}
